import { getRequest } from "../Util/utils";
import { canDownloadOriginalFile, deleteCalendarEvent, lockCalendarEvent, postActivityAttributes, type SlopeIntervals } from "@/Util/api";
import type { GroupSharingOptions, ReportKeys } from './constants';
import { isSameDay } from "date-fns";

type Externals = "strava" | "garmin"
export type ExternalLinks = Partial<Record<Externals, {url: string}>>

// exercises come in 3 types: Activity, Workout, Training.
export interface Exercise {
    name?: string,
    description?: string,
    path?: string,
    state?: string[],
    start_date?: string,
    owner?: string,

    exerciseType?: string,
    power_source?: string,
    type?: string,
    icon?: string,
    activity_mode?: string,
    repeat_type?: string,
    sharing_type?: string,
    matched_activity?: string, // either the path of the matched activity, or 'forecast' if matched to a forecast activity
    num_matches?: number,
    group_sharing?: string,
    original_file_path?: string,
    hasPowerData?: boolean,

    medal?: number,

    favorite?: boolean, // used by activities
    liked?: boolean, // used by workouts
    disliked?: boolean,

    flag?: boolean,
    scheduled?: boolean,
    is_achievement?: boolean,
    enabled?: boolean,

    session_template?: {
        video_id?: string,
        spotify_url?: string,
    },

    data_source?: string,
    device_info?: {
        manufacturer?: string,
        product?: string,
    }
    provider?: string,

    workout_id?: string, // This is used by activities, contains path of associated workout
    workout?: { // This is used by trainings (different object structure)
        path?: string,
        name?: string,
    }
    session_instance_id?: string,

    duration?: number,
    distance?: number,
    xss?: number,
    xlss?: number,
    xhss?: number,
    xpss?: number,
    adu?: number,
    difficulty?: number,
    rating?: string, // Difficulty rating, like "Easy", "Tough", or "Hard"
    spr?: string,        // Specificity field on activity
    specRating?: string, // Specificity field on training object
    focus?: string,
    fa?: string, // athlete focus, I think this should pretty much always be the same as focus
    sfd?: number, // Specificity Focus Duration in seconds
    avg_power?: number,
    max_power?: number,
    xep?: number, // Xert Equivalent Power
    avg_speed?: number,
    max_speed?: number,
    tgc?: number, // Total Grams Carbs
    tgf?: number, // Total Grams Fat
    total_intervals?: number,
    mixed_mode?: boolean, // Has slope intervals as well as ERG intervals
    slope_intervals?: SlopeIntervals | null, // How a mixed mode workout was sent to a workout player

    placeholder_xss_details?: {
        xss?: number,
        xlss?: number,
        xhss?: number,
        xpss?: number,
    }
    placeholder_name?: string,

    signature?: {
      atc: number, // Above threshold capacity, aka high intensity energy (HIE)
      ftp: number, // Functional threshold power
      ltp: number, // Lower threshold power
      pp: number, // Peak power
    }

    duplicate_group_id?: string,
    is_currently_selected_duplicate?: boolean,

    chart_url?: string,
    map_url?: string,
    workout_chart_url?: string,
    street_view_url?: string,

    // TODO: fix on backend
    // IMPORTANT!!!: these are only sometimes set, if they are not set then check the above URLs for null. Refer to getAllImageUrls function below.
    // This is most likely a difference between different exercise types (activity vs workout vs training)
    has_chart_image?: boolean,
    has_map_image?: boolean,
    has_workout_chart_image?: boolean,
    has_street_image?: boolean,

    suitability?: string, // used by training, list encoded as a string.
    
    externalLinks?: ExternalLinks,
    report_types?: ReportKeys[],

    // use canDownload() since this field is not initially set
    can_download_original_file?: boolean,

    strava_upload_id?: string, // this is just the id of the strava upload
    external_id?: string, // once an upload is successful, this is set to the strava activity id
    garmin_id?: string, // set on activities that arrived from Garmin Connect

    venue?: string, // 'virtual' or 'outdoor'

    original_placeholder_date?: string, // if this activity was swapped from a different date, this field is set to the original date. Used to show the "swapped" tag and tooltip.
    forecast_index?: number,
    related_activity_id?: string,

    // Other riders detected on this group ride (resolved from group_activity_paths on the backend).
    group_members?: GroupMember[],

    // User-defined labels, e.g. "Race" or a custom name -- activities only, see CustomActivityTags.svelte.
    // Deliberately not `tags`: that name is already Workout content-category tags ("Trial",
    // "Standard", ...), a different concept that happens to share this same Exercise type.
    custom_tags?: string[],
}

export interface GroupMember {
    path: string,        // the group member's activity path
    name: string,        // the group member's activity name
    username: string,    // the group member's username (used to derive their squadmate color)
    display_name: string,// the group member's full name
    avatar: string,      // the group member's avatar image URL

    // Squad context relative to the viewing user (resolved on the backend).
    squad_name?: string | null,   // display name of the shared squad to surface, or null
    squad_url?: string | null,    // link to that squad's community page, or null
    activity_url?: string | null, // link to the member's activity (squad-scoped when shared via a squad)
    shared_only?: boolean,        // shared their activity but shares no squad with the viewer
    invite_club?: { name: string, display_name: string } | null, // dominant group squad to invite a shared-only member to, or null

    breakthrough?: {
        medal: number, // 1 = bronze, 2 = silver, 3 = gold
        signature: { atc: number, ftp: number, pp: number },      // post-ride signature (TP/HIE/PP)
        br_signature_changes: { atc: number, ftp: number, pp: number }, // signature the breakthrough moved *from*
    } | null,
}

/* Boolean Checks */

const canDelete  = (exercise: Exercise): boolean => {return !exercise?.state?.includes("no_delete")}
const canFavorite = (exercise: Exercise): boolean => {return !exercise?.state?.includes("no_favorite")}
const canEdit = (exercise: Exercise ): boolean => {
  return (!(exercise?.hasPowerData == true) || isForecastEvent(exercise) || isPlanned(exercise) || isManual(exercise)) && !isGoalEvent(exercise)
}
const canMove = (exercise: Exercise): boolean => {return !isRecorded(exercise) && !isForecastEvent(exercise)}
const canCopy = (exercise: Exercise): boolean => {return !isForecastEvent(exercise)}
const isAnchor = (exercise: Exercise): boolean => {return !!exercise?.state?.includes("anchor")}
const isForecast = (exercise: Exercise): boolean => {return !!exercise?.state?.includes('forecast')}
const isGoalEvent = (exercise: Exercise): boolean => {return !!exercise?.state?.includes("event_A")}
const isPinned = (exercise: Exercise): boolean => {return !!exercise?.state?.includes('pinned')}
const isPlaceholder = (exercise: Exercise): boolean => {return !!exercise?.state?.includes('placeholder')}

const noActivity = (exercise: Exercise): boolean => {return (!!exercise?.state?.includes("rest")) || (!!exercise?.state?.includes("recovery"))}
const isForecastEvent = (exercise: Exercise): boolean => {return isForecast(exercise) && isPlanned(exercise)}
const isIncomplete = (exercise: Exercise): boolean => {return isForecast(exercise) && !isPlaceholder(exercise) } 
const isPlanned = (exercise: Exercise): boolean => {return exercise?.activity_mode == "planned"}
const isRecorded = (exercise: Exercise): boolean => {return exercise?.activity_mode == null}

// Manual activities are non-recorded manualEntry activities
const isManual = (exercise: Exercise): boolean => {return exercise?.activity_mode == "manual" }
// manualEntry activities can be either manual activities, or recorded activities that had data modified.
// This is used only to show the "Manual" tag, otherwise isManual should be used to differentiate from planned and recorded activities.
const isManualEntry = (exercise: Exercise): boolean => {return exercise?.activity_mode == "manual" || !!exercise?.state?.includes('manualEntry')}

const isStarred = (exercise: Exercise): boolean => {return !!exercise?.favorite}
const isFlagged = (exercise: Exercise): boolean => {return !!exercise?.flag}
const isRaceAchievement = (exercise: Exercise): boolean => {return !!exercise?.is_achievement}
const isRace = (exercise: Exercise): boolean => {return !exercise?.is_achievement}
const isRest = (exercise: Exercise): boolean => {return ((exercise.state?.includes("rest") ?? false) || (exercise.state?.includes("recovery") ?? false)) && !isGoalEvent(exercise)}

const isHeartrateDerived = (exercise: Exercise): boolean => {return exercise?.power_source === "hr_estimate"}
const repeatsDaily = (exercise: Exercise): boolean => {return exercise?.repeat_type === "Daily"}
const repeatsWeekly = (exercise: Exercise): boolean => {return exercise?.repeat_type === "Weekly"}
const isPubliclyShared = (exercise: Exercise): boolean => {return exercise?.sharing_type === "public"}

// isMatched returns true for COMPLETED activities that have been matched to a planned/forecast activity
const isMatched = (exercise: Exercise): boolean => {return exercise?.matched_activity != null && exercise?.matched_activity?.length > 0}
// hasMatch returns true for PLANNED/FORECAST activities that have at least one completed activity matched to it
const hasMatch = (exercise: Exercise): boolean => {return exercise?.num_matches > 0}

// returns true if activityA is matched to activityB
const isMatchedAtoB = (activityA: Exercise, activityB: Exercise): boolean => {
  if (!activityA.enabled) {
    return false;
  }
  if (activityA.matched_activity === 'forecast') {
    return isForecast(activityB) && isSameDay(new Date(activityA.start_date), new Date(activityB.start_date));
  }
  return activityA.matched_activity === activityB.path;
}

const isMerged = (exercise: Exercise): boolean => {return !!exercise?.state?.includes('merged')}

function isDuplicate(exercise: Exercise): boolean {
  return exercise?.duplicate_group_id && exercise.is_currently_selected_duplicate !== true;
}

function isWorkout(exercise: Exercise): boolean {
  return exercise?.exerciseType?.toLowerCase() === 'workout';
}

// If it has an activity_mode or exerciseType is 'activity', it's an activity
function isActivity(exercise: Exercise): boolean {
  return !!exercise?.activity_mode || exercise?.exerciseType?.toLowerCase() === 'activity' || exercise?.exerciseType?.toLowerCase() === 'session';
}

function isHidden(exercise: Exercise): boolean {
  return exercise?.enabled != true;
}

function isInCalculations(exercise: Exercise): boolean {
  return !Activity.isHidden(exercise) && !Activity.isDuplicate(exercise)
}

async function canDownload(exercise: Exercise): Promise<boolean> {
  if (exercise?.can_download_original_file === undefined) {
    exercise.can_download_original_file = await canDownloadOriginalFile(exercise.path);
  }
  return exercise?.can_download_original_file ?? false;
}

function isSwapped(exercise: Exercise): boolean {
  return exercise.original_placeholder_date ? (new Date(exercise.original_placeholder_date)).toDateString() !== (new Date(exercise.start_date)).toDateString() : false
}

/* Get Properties */
// * Some of these are just syntax sugar to separate the frontend from the backend. We can make them more robust as time goes on.
 

// NOTE: This is used for ActivityTag::exerciseType only.
// Use exercise.exerciseType for other uses like generating URLs.
function getExerciseType(exercise: Exercise) {
    if(!exercise?.exerciseType) return null
    switch(exercise.exerciseType) {
        case ("Session"):
            return "Session"
        case ("Workout"):
            return "Workout"
        case ("Activity"):
            if (exercise?.workout_id) return "Workout"
            else if (exercise?.session_instance_id) return "Session"
            return "Activity"
        default:
            return null
    }
}

function getBreakthroughMedal(exercise: Exercise) {
    if(!exercise?.medal) return null
    switch(exercise.medal) {
        case (-1):
            return "fake"
        case (1):
            return "bronze"
        case (2):
            return "silver"
        case (3):
            return "gold"
        default:
            return null
    }
}

function getGroupSharingType(exercise: Exercise) {
    if(!exercise?.group_sharing) return null
    switch(exercise.group_sharing) {
        case ("Group"):
            return "group"
        case ("Squad"):
            return "squad"
        case ("Public"):
            return "public"
        case ("Private"):
            return "private"
        default:
            return null
    }
}

function getType(exercise: Exercise) {
    return exercise?.type
}

function getIcon(exercise: Exercise) {
    return exercise?.icon
}

function getRaceTypeTag(exercise: Exercise) {
    if(exercise?.is_achievement === null) return null
    return exercise.is_achievement ? "Achievement" : "Race"
}

function getVideoId(exercise: Exercise): string {
    return exercise?.session_template?.video_id
}

function getYoutubeUrl(exercise: Exercise): string {
    const videoId = getVideoId(exercise)
    if(!videoId) return null
    else return `https://youtu.be/${videoId}`
}

function getSpotifyUrl(exercise: Exercise): string {
    return exercise?.session_template?.spotify_url ?? ""
}

/**
 * Link to this activity on Garmin Connect, or null if it didn't come from Garmin. Prefers the
 * externalLinks the activity endpoints build (Activity::getExternalData()), and falls back to
 * assembling the same URL from the raw fields for the payloads that don't carry externalLinks.
 */
function getGarminLink(exercise: Exercise): string | null {
  if (exercise?.externalLinks?.garmin?.url) return exercise.externalLinks.garmin.url;
  if (exercise?.provider === 'garmin' && exercise?.garmin_id) {
    return `https://connect.garmin.com/modern/activity/${exercise.garmin_id}`;
  }
  return null;
}

function getDetailsPageLink(exercise: Exercise): string | null {
  if(isActivity(exercise)) return "/activities/" + exercise.path;
  else if(isWorkout(exercise)) return "/workout/" + exercise.path + "/view";
  else return null
}

export enum ImageType {
  StreetView = "street_view",
  Map = "map",
  Chart = "chart",
  WorkoutChart = "workout_chart",
  MagicBuckets = "magic_buckets",
}

export const MAGIC_BUCKETS_ICON_URL = '/assets/images/logos/XertLogosV3/XertXMBIcon.svg'

// A manual Magic Buckets activity (made from the calendar or by a forecast plan for a long day) is named "XMB ..." and has no
// workout, so it has no chart to show: it gets the Magic Buckets icon in place of a workout image. Only planned and manual
// activities: a recorded ride or a workout named "XMB ..." keeps its own images.
function isMagicBuckets(exercise: Exercise): boolean {
  if (!(isPlanned(exercise) || isManual(exercise)) || isWorkout(exercise)) return false;
  const workoutId = exercise?.workout_id;
  return /^XMB\b/.test(exercise?.name ?? '') && (!workoutId || String(workoutId) === '0');
}

// Image priority is as follows: Magic Buckets icon -> street view -> map -> chart -> workout chart
function getOneImage(exercise: Exercise): { url: string, type: ImageType } | null {
  if (isMagicBuckets(exercise)) return { url: MAGIC_BUCKETS_ICON_URL, type: ImageType.MagicBuckets };
  let imageUrl: string | null = null
  let imageType: ImageType | null = null
  if (exercise?.has_street_image !== false && exercise?.street_view_url) {
    imageUrl = exercise.street_view_url ?? null;
    imageType = ImageType.StreetView;
  } else if (exercise?.has_map_image !== false && exercise?.map_url) {
    imageUrl = exercise.map_url ?? null;
    imageType = ImageType.Map;
  } else if (exercise?.has_chart_image !== false && exercise?.chart_url) {
    imageUrl = exercise.chart_url ?? null;
    imageType = ImageType.Chart;
  } else if (exercise?.has_workout_chart_image !== false && exercise?.workout_chart_url) {
    imageUrl = exercise.workout_chart_url ?? null;
    imageType = ImageType.WorkoutChart;
  }
  return imageUrl ? { url: imageUrl.replace(/^.*(?=\/assets)/, ''), type: imageType } : null;
}

function getAllImages(exercise: Exercise): { url: string, type: ImageType }[] {
  if (isMagicBuckets(exercise)) return [{ url: MAGIC_BUCKETS_ICON_URL, type: ImageType.MagicBuckets }];
  const images = [
    { url: exercise?.has_street_image !== false ? exercise?.street_view_url ?? null : null, type: ImageType.StreetView },
    { url: exercise?.has_map_image !== false ? exercise?.map_url ?? null : null, type: ImageType.Map },
    { url: exercise?.has_chart_image !== false ? exercise?.chart_url ?? null : null, type: ImageType.Chart },
    { url: exercise?.has_workout_chart_image !== false ? exercise?.workout_chart_url ?? null : null, type: ImageType.WorkoutChart },
  ]
  
  return images.map((image) => image?.url ? { url: image.url?.replace(/^.*(?=\/assets)/, ''), type: image.type } : null).filter(image => image?.url);
}

// TODO (hyde): personalize error images to the activity
function getBackupImageUrl(exercise?: Exercise): string {
  return '/assets/images/graph.jpg';
}

function getWorkoutPath(exercise: Exercise): string | null {
  return exercise?.workout_id || exercise?.workout?.path || null;
}

function getSpecRating(exercise: Exercise): string | null {
  return exercise?.specRating || exercise?.spr || null;
}

function getPlaceholderName(exercise: Exercise): string {
  return exercise.placeholder_name || exercise.name;
}

/* Functions */
async function clearFromCalendar(exercise) {
    if(!exercise) return;
    if(exercise.isSession) { // TODO: Does this ever run I cannot find where isSession is set
        return window.fetch("/session-instance/" + exercise.session_id, {...getRequest(), method: 'DELETE'});        
    }
    // TODO: Remove selected state if no longer used.
    if (exercise.state?.includes('selected')) {
        // clearSelectedExercise()
    } else {
        return deleteCalendarEvent(exercise.path);
    }
}

function leaveSession(exercise) {
    if(!exercise) return;
    return window.fetch(`/session/${exercise.session_id}/leave`, {...getRequest(), method: 'POST'});
}

async function togglePin(exercise) {
    if(!exercise) return;
    // console.log('togglePin')

    if(!isPinned(exercise)){
        exercise?.state?.push("pinned") ?? (exercise.state = ["pinned"]);
        exercise.state = exercise.state
    }   
    else{
        exercise.state = exercise?.state?.filter(s=>s!="pinned") ?? [];
    }
    
    let body = {path:exercise.path, isPinned: isPinned(exercise)}
    return lockCalendarEvent(body).catch((e)=>{
        if(!isPinned(exercise)){
            exercise?.state?.push("pinned") ?? (exercise.state = ["pinned"]);
            exercise.state = exercise.state
        }   
        else{
            exercise.state = exercise?.state?.filter(s=>s!="pinned") ?? [];
        }
        })
}

async function toggleFlag(exercise: Exercise) {
    const flag = !isFlagged(exercise) 
    return postActivityAttributes({path: exercise.path, "flag": flag ? 1 : 0}).then(() => exercise.flag = flag)
}

function getDuplicateGroupHead(exercise: Exercise, allExercises: Exercise[]): Exercise | null {
  if(!exercise?.duplicate_group_id) return null;
  const duplicateGroupHead = allExercises.find(e => e.duplicate_group_id == exercise.duplicate_group_id && e.is_currently_selected_duplicate);
  if(!duplicateGroupHead) return null;
  return duplicateGroupHead;
}

async function toggleFavorite(exercise: Exercise) {
    const star = !isStarred(exercise)
    return postActivityAttributes({path: exercise.path, "favourite": star ? 1 : 0}).then(() => exercise.favorite = star)
}

async function updateGroupSharing(exercise: Exercise, sharingType: GroupSharingOptions) {
    return postActivityAttributes({path: exercise.path, "group_sharing": sharingType}).then(() => {exercise.group_sharing = sharingType})
}

async function updateName(exercise: Exercise, name: string) {    
    return postActivityAttributes({path: exercise.path, "name": name}).then(() => {exercise.name = name})
}

async function updateDescription(exercise: Exercise, description: string) {
    return postActivityAttributes({path: exercise.path, "desc": description}).then(() => {exercise.description = description})
}

function downloadFileHref(exercise: Exercise) {
    return `/activities/download/${exercise.path}`
}

export const Activity = {
    // Boolean Checks
    isPinned,
    isPlaceholder,
    isPlanned,
    isRecorded,
    isStarred,
    isFlagged,
    getYoutubeUrl,
    getSpotifyUrl,
    noActivity,
    isForecastEvent,
    isIncomplete,
    isGoalEvent,
    canDelete,
    canFavorite,
    canEdit,
    canMove,
    canCopy,
    isAnchor,
    isManual,
    isManualEntry,
    isHeartrateDerived,
    repeatsDaily,
    repeatsWeekly,
    isPubliclyShared,
    isRaceAchievement,
    isRace,
    isForecast,
    isRest,
    isMatched,
    hasMatch,
    isDuplicate,
    isWorkout,
    isActivity,
    isHidden,
    isInCalculations,
    canDownload,
    isMerged,
    isMatchedAtoB,
    isSwapped,

    /* Get Properties */
    getExerciseType,
    getBreakthroughMedal,
    getGroupSharingType,
    getType,
    getIcon,
    getRaceTypeTag,
    getVideoId,
    getDetailsPageLink,
    getGarminLink,
    isMagicBuckets,
    getOneImage,
    getAllImages,
    getBackupImageUrl,
    getWorkoutPath,
    getSpecRating,
    getPlaceholderName,

    // Functions
    clearFromCalendar,
    
    leaveSession,

    toggleFlag,
    toggleFavorite,
    togglePin,
    getDuplicateGroupHead,
    updateGroupSharing,
    updateName,
    updateDescription,
    downloadFileHref,
}
